import axios from "axios";

// API Base URL Configuration with intelligent defaults
// Priority: 1. Environment variable, 2. Smart detection based on hostname, 3. Development fallback
const getApiBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Smart detection based on hostname
  const hostname = window.location.hostname;
  
  // Production: admin.sccnhub.com - Nginx proxies /api to backend
  if (hostname.includes('admin.sccnhub.com')) {
    return `https://${hostname}`; // Uses same domain, nginx handles /api proxy
  }
  
  // Development: localhost or 127.0.0.1
  return 'http://localhost:5000';
};

const BACKEND_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("adminToken") || localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const handleError = (error) => {
  if (error.response) {
    console.error("API Error Response:", error.response.data);
    throw new Error(
      error.response.data.error ||
        error.response.data.message ||
        "An error occurred"
    );
  } else if (error.request) {
    console.error("API No Response:", error.request);
    throw new Error(
      "No response from server. Please check your network connection."
    );
  } else {
    console.error("API Request Error:", error.message);
    throw new Error(
      error.message || "An error occurred while setting up the request"
    );
  }
};

// Helper function to extract data from new API response format
const extractData = (response) => {
  // Handle new format: { success: true, data: [...] }
  if (response && response.success && response.data !== undefined) {
    return response.data;
  }
  // Fallback to old format
  return response;
};

const blogService = {
  getAllBlogs: async () => {
    try {
      const response = await api.get("/api/blogs");
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  // Paginated fetch for blogs. Returns { data: [...], pagination: {...} }
  getBlogs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);

      const response = await api.get(`/api/blogs?${queryParams.toString()}`);
      const result = extractData(response.data);

      if (result && result.data && Array.isArray(result.data)) {
        return { data: result.data, pagination: result.pagination || {} };
      }

      // Fallback: if API returned an array
      return { data: Array.isArray(result) ? result : [], pagination: {} };
    } catch (error) {
      return handleError(error);
    }
  },
  getBlogById: async (id) => {
    try {
      const response = await api.get(`/api/blogs/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  createBlog: async (formData) => {
    try {
      const response = await api.post("/api/blogs", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateBlog: async (id, formData) => {
    try {
      const response = await api.put(`/api/blogs/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteBlog: async (id) => {
    try {
      const response = await api.delete(`/api/blogs/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const analyticsService = {
  getContentPerformance: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.range) queryParams.append('range', params.range);
      
      const response = await api.get(`/api/analytics/performance?${queryParams.toString()}`);
      const result = extractData(response.data);
      
      // Ensure we always return data in the expected format
      return {
        days: result?.days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        blogViews: result?.blogViews || [0, 0, 0, 0, 0, 0, 0],
        newsletterOpens: result?.newsletterOpens || [0, 0, 0, 0, 0, 0, 0],
        totalBlogViews: result?.totalBlogViews || 0,
        totalNewsletterOpens: result?.totalNewsletterOpens || 0,
        contentStats: result?.contentStats || {
          totalBlogs: 0,
          totalNewsletters: 0,
          activeBlogs: 0,
          activeNewsletters: 0
        }
      };
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      // Return default data structure if API fails
      return {
        days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        blogViews: [0, 0, 0, 0, 0, 0, 0],
        newsletterOpens: [0, 0, 0, 0, 0, 0, 0],
        totalBlogViews: 0,
        totalNewsletterOpens: 0,
        contentStats: {
          totalBlogs: 0,
          totalNewsletters: 0,
          activeBlogs: 0,
          activeNewsletters: 0
        }
      };
    }
  },
  
  getAnalyticsSummary: async () => {
    try {
      const response = await api.get("/api/analytics/summary");
      return extractData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics summary:', error);
      return {
        totalBlogs: 0,
        totalNewsletters: 0,
        totalBlogViews: 0,
        totalNewsletterOpens: 0,
        averageBlogViews: 0,
        averageNewsletterOpens: 0
      };
    }
  },
  
  trackBlogView: async (blogId) => {
    try {
      const response = await api.post(`/api/analytics/track/blog-view/${blogId}`);
      return extractData(response.data);
    } catch (error) {
      console.error('Failed to track blog view:', error);
      // Don't throw error for tracking failures as they shouldn't break the UI
      return { success: false };
    }
  },
  
  trackNewsletterOpen: async (newsletterId) => {
    try {
      const response = await api.post(`/api/analytics/track/newsletter-open/${newsletterId}`);
      return extractData(response.data);
    } catch (error) {
      console.error('Failed to track newsletter open:', error);
      // Don't throw error for tracking failures as they shouldn't break the UI
      return { success: false };
    }
  }
};

const newsService = {
  getAllNews: async () => {
    try {
      const response = await api.get("/api/news");
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  getNewsById: async (id) => {
    try {
      const response = await api.get(`/api/news/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  createNews: async (formData) => {
    try {
      const response = await api.post("/api/news", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateNews: async (id, formData) => {
    try {
      const response = await api.put(`/api/news/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteNews: async (id) => {
    try {
      const response = await api.delete(`/api/news/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const mentorService = {
  getAllMentors: async () => {
    try {
      const response = await api.get("/api/mentors");
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  getMentorById: async (id) => {
    try {
      const response = await api.get(`/api/mentors/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  createMentor: async (formData) => {
    try {
      const response = await api.post("/api/mentors", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateMentor: async (id, formData) => {
    try {
      const response = await api.put(`/api/mentors/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteMentor: async (id) => {
    try {
      const response = await api.delete(`/api/mentors/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const teamMemberService = {
  getAllTeamMembers: async () => {
    try {
      const response = await api.get("/api/team-members");
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  getTeamMemberById: async (id) => {
    try {
      const response = await api.get(`/api/team-members/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
   createTeamMember: async (formData) => {
    try {
      const response = await api.post("/api/team-members", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateTeamMember: async (id, formData) => {
    try {
      // Convert FormData to plain object for debugging
      const payload = {};
      for (const [key, value] of formData.entries()) {
        payload[key] = value;
      }
      console.log('Update payload:', payload);

      const response = await api.put(`/api/team-members/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        transformRequest: (data, headers) => {
          // Don't JSON.stringify FormData
          return data;
        }
      });

      return response.data;
    } catch (error) {
      console.error('Update error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      throw error;
    }
  },
  deleteTeamMember: async (id) => {
    try {
      const response = await api.delete(`/api/team-members/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const latestUpdateService = {
  getAllLatestUpdates: async () => {
    try {
      const response = await api.get("/api/latest-updates");
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  getLatestUpdateById: async (id) => {
    try {
      const response = await api.get(`/api/latest-updates/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  createLatestUpdate: async (formData) => {
    try {
      const response = await api.post("/api/latest-updates", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateLatestUpdate: async (id, formData) => {
    try {
      const response = await api.put(`/api/latest-updates/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteLatestUpdate: async (id) => {
    try {
      const response = await api.delete(`/api/latest-updates/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const contactService = {
  getAllContacts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.status) queryParams.append("status", params.status);
      if (params.priority) queryParams.append("priority", params.priority);
      if (params.search) queryParams.append("search", params.search);
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const response = await api.get(`/api/contact?${queryParams.toString()}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  getContactById: async (id) => {
    try {
      const response = await api.get(`/api/contact/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateContactStatus: async (id, statusData) => {
    try {
      const response = await api.put(`/api/contact/${id}/status`, statusData);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteContact: async (id) => {
    try {
      const response = await api.delete(`/api/contact/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  getDashboardStats: async () => {
    try {
      const response = await api.get("/api/contact/stats/dashboard");
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  testConnection: async () => {
    try {
      const response = await api.get("/api/contact/test");
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const careerService = {
  getAllCareers: async (params = {}) => {
    try {
      const response = await api.get("/api/careers", { params });
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  getCareerBySlug: async (slug) => {
    try {
      const response = await api.get(`/api/careers/slug/${slug}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  getCareerById: async (id) => {
    try {
      const response = await api.get(`/api/careers/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  createCareer: async (data) => {
    try {
      const response = await api.post("/api/careers", data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateCareer: async (id, data) => {
    try {
      const response = await api.put(`/api/careers/${id}`, data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteCareer: async (id) => {
    try {
      const response = await api.delete(`/api/careers/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  toggleCareerStatus: async (id) => {
    try {
      const response = await api.patch(`/api/careers/${id}/status`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const newsletterService = {
  getAllNewsletters: async () => {
    try {
      const response = await api.get("/api/newsletters");
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  getNewslettersByType: async (type, params = null) => {
    try {
      // allow optional pagination params via second argument (backward compatible)
      let query = '';
      // If caller passed an object like getNewslettersByType(type, {page:1, limit:10}) we won't break
      if (params && typeof params === 'object') {
        const qp = new URLSearchParams();
        if (params.page) qp.append('page', params.page);
        if (params.limit) qp.append('limit', params.limit);
        if (params.search) qp.append('search', params.search);
        query = qp.toString() ? `?${qp.toString()}` : '';
      }

      const response = await api.get(`/api/newsletters/type/${type}${query}`);
      const result = extractData(response.data);

      if (result && result.data && Array.isArray(result.data)) {
        return { data: result.data, pagination: result.pagination || {} };
      }

      return { data: Array.isArray(result) ? result : [], pagination: {} };
    } catch (error) {
      return handleError(error);
    }
  },
  getNewsletterById: async (id) => {
    try {
      const response = await api.get(`/api/newsletters/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  createNewsletter: async (formData) => {
    try {
      const response = await api.post("/api/newsletters", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateNewsletter: async (id, formData) => {
    try {
      const response = await api.put(`/api/newsletters/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteNewsletter: async (id) => {
    try {
      const response = await api.delete(`/api/newsletters/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const eventService = {
  getAllEvents: async () => {
    try {
      const response = await api.get("/api/events");
      const data = extractData(response.data);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return handleError(error);
    }
  },
  getEventById: async (id) => {
    try {
      const response = await api.get(`/api/events/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  createEvent: async (formData) => {
    try {
      const response = await api.post("/api/events", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  updateEvent: async (id, formData) => {
    try {
      const response = await api.put(`/api/events/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  deleteEvent: async (id) => {
    try {
      const response = await api.delete(`/api/events/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post("/api/auth/login", { email, password });
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  logout: async () => {
    try {
      const response = await api.post("/api/auth/logout");
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  getCurrentAdmin: async () => {
    try {
      const response = await api.get("/api/auth/me");
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const universityService = {
  getAllUniversities: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append("status", params.status);
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.search) queryParams.append("search", params.search);

      const response = await api.get(
        `/api/universities?${queryParams.toString()}`
      );
      const result = extractData(response.data);

      // Handle the nested data structure: { data: [...], pagination: {...} }
      if (result && result.data && Array.isArray(result.data)) {
        return {
          data: result.data,
          pagination: result.pagination || {},
        };
      }
      return {
        data: Array.isArray(result) ? result : [],
        pagination: {},
      };
    } catch (error) {
      return handleError(error);
    }
  },
  approveUniversity: async (id, data = {}) => {
    try {
      const response = await api.put(`/api/universities/${id}/approve`, data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  rejectUniversity: async (id, data) => {
    try {
      const response = await api.put(`/api/universities/${id}/reject`, data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const studentService = {
  getAllStudents: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append("status", params.status);
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.search) queryParams.append("search", params.search);

      const response = await api.get(`/api/students?${queryParams.toString()}`);
      const result = extractData(response.data);

      // Handle the nested data structure: { data: [...], pagination: {...} }
      if (result && result.data && Array.isArray(result.data)) {
        return {
          data: result.data,
          pagination: result.pagination || {},
        };
      }
      return {
        data: Array.isArray(result) ? result : [],
        pagination: {},
      };
    } catch (error) {
      return handleError(error);
    }
  },
  approveStudent: async (id, data = {}) => {
    try {
      const response = await api.put(`/api/students/${id}/approve`, data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  rejectStudent: async (id, data) => {
    try {
      const response = await api.put(`/api/students/${id}/reject`, data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  toggleStudentStatus: async (id, data) => {
    try {
      const response = await api.put(`/api/students/${id}/toggle-status`, data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const partnershipService = {
  getAll: async () => {
    try {
      const response = await api.get("/api/partnerships");
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  getById: async (id) => {
    try {
      const response = await api.get(`/api/partnerships/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  update: async (id, data) => {
    try {
      const response = await api.patch(`/api/partnerships/${id}`, data);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  delete: async (id) => {
    try {
      const response = await api.delete(`/api/partnerships/${id}`);
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
  getStats: async () => {
    try {
      const response = await api.get("/api/partnerships/stats/summary");
      return extractData(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

export {
  blogService,
  newsService,
  careerService,
  mentorService,
  teamMemberService,
  latestUpdateService,
  contactService,
  newsletterService,
  eventService,
  authService,
  universityService,
  studentService,
  analyticsService,
  partnershipService,
  api,
};